/**
 * ClientContext — C8 wizard refactor
 *
 * Reemplaza la pantalla bloqueante /tenant-selection por un dropdown topbar.
 *
 * Estado:
 *   - clients: lista de clients accesibles
 *   - activeClientId: client activo (puede ser null si nunca se ha elegido)
 *   - activeClient: objeto completo del client activo (o null)
 *   - bootstrapLoading: true mientras se carga el estado inicial
 *   - needsClientSelection: true si hay >1 client y no hay last_client_id
 *
 * Flujo:
 *   1. Post-login → useEffect llama a GET /api/auth/bootstrap
 *   2. Bootstrap devuelve {user, clients[], active_client_id}
 *   3. Si active_client_id → hacer switch automático (emitir JWT nuevo)
 *   4. Si null y clients.length > 1 → mostrar FirstUseClientPicker (inline)
 *   5. Si null y clients.length === 1 → auto-switch (caso MSSP simple)
 *   6. Si null y clients.length === 0 → sin clients asignados
 *
 * NULL safety: last_client_id puede ser null en datos legacy → needsClientSelection=true
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import apiClient from '@/lib/axiosInstance';
import { parseApiError } from '@/lib/utils';
import { storeTokens } from '@/lib/session';

const ClientContext = createContext(null);

export const useClient = () => {
  const context = useContext(ClientContext);
  if (!context) {
    throw new Error('useClient must be used within a ClientProvider');
  }
  return context;
};

export const ClientProvider = ({ children }) => {
  const [clients, setClients] = useState([]);
  const [activeClientId, setActiveClientId] = useState(null);
  const [activeClient, setActiveClient] = useState(null);
  const [bootstrapLoading, setBootstrapLoading] = useState(true);
  const [bootstrapError, setBootstrapError] = useState(null);

  // needsClientSelection: true si hay más de 1 client y no hay active_client_id
  // (primer login con varios clients — muestra FirstUseClientPicker UNA VEZ)
  const needsClientSelection =
    !bootstrapLoading && !activeClientId && clients.length > 1;

  // Carga bootstrap al montar (o cuando el token cambia)
  const loadBootstrap = useCallback(async () => {
    // Solo cargar si hay token
    const token = localStorage.getItem('access_token');
    if (!token) {
      setBootstrapLoading(false);
      return;
    }

    setBootstrapLoading(true);
    setBootstrapError(null);

    try {
      const { data } = await apiClient.get('/auth/bootstrap');
      const { clients: clientList, active_client_id } = data;

      setClients(clientList ?? []);

      if (active_client_id) {
        // Hacer switch para obtener JWT con cid correcto
        await _performSwitch(active_client_id, clientList ?? []);
      } else if ((clientList ?? []).length === 1) {
        // Auto-switch si solo hay 1 client
        await _performSwitch(clientList[0].id, clientList);
      } else {
        // Múltiples clients sin selección → mostrar picker
        setActiveClientId(null);
        setActiveClient(null);
      }
    } catch (err) {
      console.error('[ClientContext] bootstrap error:', err);
      setBootstrapError(parseApiError(err?.response?.data?.detail, 'No se pudieron cargar los clientes de tu cuenta'));
    } finally {
      setBootstrapLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    loadBootstrap();
  }, [loadBootstrap]);

  // Hacer switch de client: llama al endpoint existente que emite JWT nuevo
  const _performSwitch = async (clientId, clientList, { rethrow = false } = {}) => {
    try {
      const { data } = await apiClient.post(`/clients/${clientId}/switch`);
      // El endpoint devuelve new_token (el JWT ya lleva dentro el cliente
      // activo). Se guarda por lib/session como todos los demas (FE-11).
      if (data.new_token) {
        storeTokens({ accessToken: data.new_token });
      }
      const found =
        (clientList ?? clients).find((c) => c.id === clientId) ??
        data.client ??
        null;
      setActiveClientId(clientId);
      setActiveClient(found);
      // Compatibilidad legacy: algunos componentes aún leen sessionStorage
      if (found) {
        sessionStorage.setItem('selectedTenant', JSON.stringify(found));
      }
      sessionStorage.setItem('isAuthenticated', 'true');
    } catch (err) {
      // No bloquear — dejar que el usuario elija manualmente (en el arranque
      // eso ya es visible: aparece el selector de cliente).
      setActiveClientId(null);
      setActiveClient(null);
      // Si lo pidió el usuario, el que llama (ClientSwitcher /
      // FirstUseClientPicker) tiene que poder enseñarle el fallo.
      if (rethrow) throw err;
    }
  };

  /**
   * setActiveClient(id) — llamado por ClientSwitcher cuando el usuario elige
   * un client diferente desde el dropdown topbar.
   */
  const switchClient = useCallback(
    async (clientId) => {
      if (clientId === activeClientId) return;
      await _performSwitch(clientId, clients, { rethrow: true });
    },
    [activeClientId, clients] // eslint-disable-line
  );

  const value = {
    clients,
    activeClientId,
    activeClient,
    bootstrapLoading,
    bootstrapError,
    needsClientSelection,
    switchClient,
    reloadBootstrap: loadBootstrap,
  };

  return (
    <ClientContext.Provider value={value}>{children}</ClientContext.Provider>
  );
};

export default ClientContext;
